import { REQUEST_STATUS } from "@/ts/enums/RequestStatus";
import { getBaseRoute } from "./route";

const paramGradeIds: Record<number, string> = {
  120: "g12",
  110: "g11",
  100: "g10",
  90: "g9",
  80: "g8",
  70: "g7",
  60: "g6",
  50: "g5",
  40: "g4",
  30: "g3",
  20: "g2",
  10: "g1",
  1: "f",
  901: "p" // preparatory
};

export const mapGradeCodeToGradeParamId = (
  gradeCode: number
): string | undefined => paramGradeIds[gradeCode];

// ---- reverse map ----
const gradeCodeByParamId: Record<string, number> = Object.fromEntries(
  Object.entries(paramGradeIds).map(([code, param]) => [param, Number(code)])
);

export const mapGradeParamIdToGradeCode = (
  paramId: string
): number | undefined => gradeCodeByParamId[paramId];

export const stripPrefix = (value: string, prefix: string) =>
  value.startsWith(prefix) ? value.slice(prefix.length) : value;

/**
 * Convert a subject name into a URL-safe slug.
 * Used to build the subject segment of assignment detail URLs.
 *
 * Examples:
 *   "English"                        → "english"
 *   "Health and Physical Education"  → "health-and-physical-education"
 *   "Science & Technology"           → "science-technology"
 */
export const slugifySubject = (subject: string): string =>
  subject
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-") // Any run of non-alphanumerics → single hyphen
    .replace(/^-|-$/g, "");      // Trim leading/trailing hyphen

export const normalizeBase = (base: string) => {
  let s = base || "/";

  // Trim trailing slashes down to at most 1 (but never trim the single root "/")
  let end = s.length;
  while (end > 1 && s.charCodeAt(end - 1) === 47 /* '/' */) {
    end--;
  }
  s = s.slice(0, end);

  // Ensure exactly one trailing slash
  return s.endsWith("/") ? s : s + "/";
};

export const replaceGradeInPath = (pathname: string, nextGrade: string) => {
  const b = normalizeBase(getBaseRoute());
  if (!pathname.startsWith(b)) return `${b}${nextGrade}`;

  const rest = pathname.slice(b.length); // after "/learning/"
  const parts = rest.split("/"); // ["g10","terms","..."]

  if (!parts[0]) return `${b}${nextGrade}`; // landing at base
  parts[0] = nextGrade;

  return b + parts.join("/");
};

export const ensureGradeInPath = (pathname: string, grade: string) => {
  const fallbackSubpath = "not-found";
  const b = normalizeBase(getBaseRoute());

  if (!pathname.startsWith(b))
    return `${b}/${fallbackSubpath}`.replace(/\/$/, "");

  const rest = pathname.slice(b.length);
  const parts = rest.split("/");

  if (!parts[0]) return `${b}/${fallbackSubpath}`.replace(/\/$/, "");
  return replaceGradeInPath(pathname, grade);
};

export const isTermsReadyForGrade = (
  getTermsSummaryRequestStatus: REQUEST_STATUS,
  forGradeCode: number | undefined,
  expectedGradeCode: number | undefined,
  termsLength?: number | undefined
) =>
  !!expectedGradeCode &&
  forGradeCode === expectedGradeCode &&
  // either we have fresh success OR we at least have cached data
  (getTermsSummaryRequestStatus === REQUEST_STATUS.SUCCESS ||
    (termsLength && termsLength > 0));

export const isSubjectsReadyForGrade = (
  getSubjectsSummaryRequestStatus: REQUEST_STATUS,
  forGradeCode: number | undefined,
  expectedGradeCode: number | undefined,
  subjectsLength?: number | undefined
) =>
  !!expectedGradeCode &&
  forGradeCode === expectedGradeCode &&
  // either we have fresh success OR we at least have cached data
  (getSubjectsSummaryRequestStatus === REQUEST_STATUS.SUCCESS ||
    (subjectsLength && subjectsLength > 0));

export const extractTermIdFromLessonSlug = (slug: string): string | null => {
  const m = slug.match(/(?:^|-)term-(\d+)(?:-|$)/i);
  return m ? m[1] : null;
};
